/* @jsxImportSource @emotion/react */
/* eslint-disable formatjs/no-literal-string-in-jsx */
import React from 'react'
import { css, keyframes } from '@emotion/react'
import { useIntl } from 'react-intl'

import { SliteLogo } from '@slite/components'

import { Button } from '../../../components/Button/Button'
import { getCustomerQuote } from '../../../components/customerQuotes'
import { QuoteWithOrbit } from '../../../components/QuoteWithOrbit'
import { Section } from '../../../components/Section'
import { TextLayout } from '../../../components/TextLayout'
import { WebsiteText } from '../../../components/WebsiteText'
import { useRoutes } from '../../../routes'
import { SMALL_BREAKPOINT } from '../../../utils/breakpoints'
import {
  desktopOnlyImportantCss,
  mobileOnlyCss,
} from '../../../utils/responsive'
import { CARD_SHADOW } from '../../../utils/shadows'
import mcpIllustration from '../assets/mcp.svg'
import { ClientChatGPT } from './ClientChatGPT'
import { ClientClaudeCode } from './ClientClaudeCode'
import { ClientCursor } from './ClientCursor'
import { SliteClient } from './ClientSlite'
import { McpSecurity } from './McpSecurity'
import { McpTools } from './McpTools'

export function SectionHomeMcp({
  hideCta,
  hideQuote,
}: {
  hideCta?: boolean
  hideQuote?: boolean
} = {}) {
  const intl = useIntl()
  const routes = useRoutes()
  // Alexis / Agorapulse explicitly mentions Slite MCP on Claude — perfect
  // tonal match for this section.
  const quote = getCustomerQuote('AlexisDupontAgorapulseMCP', intl)

  const content = {
    title: intl.formatMessage({
      id: 'home.mcp.title',
      defaultMessage: 'The context layer for AI agents you already use',
    }),
    description: intl.formatMessage({
      id: 'home.mcp.description',
      defaultMessage:
        'The same engine that powers the Agent is exposed via MCP. Your verified knowledge becomes the source of truth for Claude, ChatGPT, Cursor, and anything else your company plugs in.',
    }),
    button: hideCta ? null : (
      <Button href={routes.mcp()} variant="secondary" withChevron>
        {intl.formatMessage({
          id: 'home.mcp.cta',
          defaultMessage: 'Slite MCP',
        })}
      </Button>
    ),
    illustration: <img src={mcpIllustration} alt="" />,
    clients: [
      {
        illustration: <SliteClient />,
      },
      {
        illustration: <ClientClaudeCode />,
      },
      {
        illustration: <ClientCursor />,
      },
      {
        illustration: <ClientChatGPT />,
      },
    ],
    cards: [
      {
        title: intl.formatMessage({
          id: 'home.mcp.cards.1.title',
          defaultMessage: 'Full read & write access',
        }),
        description: intl.formatMessage({
          id: 'home.mcp.cards.1.description',
          defaultMessage:
            'Agents can browse, draft, and propose changes; humans approve every write',
        }),
        illustration: (
          <div style={{ marginTop: -20 }}>
            <McpTools />
          </div>
        ),
      },
      {
        title: intl.formatMessage({
          id: 'home.mcp.cards.2.title',
          defaultMessage: 'Permission-aware at the API level',
        }),
        description: intl.formatMessage({
          id: 'home.mcp.cards.2.description',
          defaultMessage:
            'Third-party agents inherit the same permissions your team works under.',
        }),
        illustration: <McpSecurity />,
      },
    ],
  }

  // x-position of each client card's center: 4 equal flex columns with 20px gaps.
  // Logo box sits at 50%; each connector runs horizontally to a card center, then down.
  const connectors = [
    {
      side: 'left',
      left: 'calc((100% - 60px) / 8)',
      width: 'calc(50% - (100% - 60px) / 8)',
    },
    {
      side: 'left',
      left: 'calc(3 * (100% - 60px) / 8 + 20px)',
      width: 'calc(50% - 3 * (100% - 60px) / 8 - 20px)',
    },
    {
      side: 'right',
      left: '50%',
      width: 'calc(5 * (100% - 60px) / 8 + 40px - 50%)',
    },
    {
      side: 'right',
      left: '50%',
      width: 'calc(7 * (100% - 60px) / 8 + 60px - 50%)',
    },
  ]
  const dashed = '2px dashed rgba(0, 0, 0, 0.15)'

  // Mobile-only marquee for the client cards: original 280px width, looping
  // horizontal scroll. Duplicate the list so translateX(-50%) loops seamlessly.
  const marqueeAnimation = keyframes`
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
  `
  // Full-bleed marquee container: 100vw with a negative left margin pulling
  // it past the Section's horizontal padding so the clip lines up with the
  // viewport edge. Clip only horizontally so the card box-shadows can render
  // above and below the track (overflow-x: hidden would force overflow-y to
  // auto and clip the shadow).
  const mobileMarqueeContainerCss = css`
    ${mobileOnlyCss};
    @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
      width: 100vw;
      margin-left: calc(50% - 50vw);
      overflow-x: clip;
      overflow-y: visible;
    }
  `
  const marqueeTrackCss = css`
    display: flex;
    gap: 16px;
    width: max-content;
    animation: ${marqueeAnimation} 30s linear infinite;
  `

  return (
    <Section
      background="moon-dust-lighter"
      containerVerticalPadding={120}
      className="text-align-center"
    >
      <TextLayout
        type="default"
        title={content.title}
        titleMaxWidth={500}
        illustration={content.illustration}
      />
      <div style={{ marginTop: 40 }}>
        <div
          css={desktopOnlyImportantCss}
          style={{ position: 'relative', height: 140 }}
        >
          <div
            className="background-shade-00"
            style={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 80,
              height: 80,
              borderRadius: 22,
              zIndex: 1,
            }}
          >
            <SliteLogo
              variant="symbol"
              style={{ width: '100%', height: '100%' }}
            />
          </div>
          {connectors.map((connector, index) => (
            <div
              key={index}
              style={{
                position: 'absolute',
                top: 40,
                left: connector.left,
                width: connector.width,
                height: 100,
                borderTop: dashed,
                pointerEvents: 'none',
                ...(connector.side === 'right'
                  ? { borderRight: dashed, borderTopRightRadius: 40 }
                  : { borderLeft: dashed, borderTopLeftRadius: 40 }),
              }}
            />
          ))}
        </div>
        {/* Desktop: 4 cards stretched into a row with connectors from the logo. */}
        <div
          css={desktopOnlyImportantCss}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}
        >
          {content.clients.map((client, index) => (
            <div
              key={index}
              style={{
                minWidth: 200,
                flex: 1,
                display: 'flex',
                background: '#fff',
                borderRadius: 12,
                overflow: 'hidden',
                boxShadow: CARD_SHADOW,
              }}
            >
              {client.illustration}
            </div>
          ))}
        </div>
        {/* Mobile: full-bleed marquee. Cards keep their natural 280px width;
            duplicated once so translateX(-50%) wraps seamlessly. */}
        <div css={mobileMarqueeContainerCss}>
          <div css={marqueeTrackCss}>
            {[...content.clients, ...content.clients].map((client, index) => (
              <div
                key={index}
                style={{
                  width: 280,
                  flexShrink: 0,
                  display: 'flex',
                  background: '#fff',
                  borderRadius: 12,
                  overflow: 'hidden',
                  boxShadow:
                    '0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 2px 6px 0 rgba(0, 0, 0, 0.05), 0 4px 12px 0 rgba(0, 0, 0, 0.01)',
                }}
              >
                {client.illustration}
              </div>
            ))}
          </div>
        </div>

        <div
          css={css`
            margin-top: 80px;
            display: flex;
            align-items: start;
            gap: 20px;
            @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
              flex-direction: column;
              gap: 60px
            }
          `}
        >
          <div
            style={{
              flex: 1,
              textAlign: 'left',
              display: 'flex',
              flexDirection: 'column',
              gap: 32,
              paddingRight: 32,
            }}
          >
            <WebsiteText
              variant="body-lg"
              className="shade-00"
              style={{
                textAlign: 'start',
              }}
            >
              {content.description}
            </WebsiteText>

            {content.button && <div>{content.button}</div>}
          </div>
          <div
            css={css`
              flex: 1;
              overflow: hidden;
              @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
                width: 100%;
              }
            `}
          >
            <McpTools />
          </div>
        </div>
        {!hideQuote && (
          <div
            className="flexbox flexbox-centered"
            style={{
              marginTop: 120,
              maxWidth: 900,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            <QuoteWithOrbit
              name={quote.name}
              title={quote.title}
              testimonial={quote.testimonial}
              pictureUrl={quote.pictureUrl}
              companyImageUrl="https://cdn.sanity.io/images/dnsgqii5/production/43953767b2a5953d0ca10ce435744a105871add3-120x120.png"
              className="grid-span-10-large grid-span-12"
            />
          </div>
        )}
      </div>
    </Section>
  )
}
